import { usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'
import { type Locale } from '@/constants/locales'
import { Routes } from '@/constants/routes'
import { type BreadcrumbItem } from '@/components/design-system/Breadcrumb'

interface UseBreadcrumbsOptions {
  includeHome?: boolean
  homeLabel?: string
  customLabels?: Record<string, string>
}

export function useBreadcrumbs(options: UseBreadcrumbsOptions = {}) {
  const pathname = usePathname()
  const locale = useLocale() as Locale
  
  const { 
    includeHome = true, 
    homeLabel = 'Home',
    customLabels = {}
  } = options

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = []
    
    // Add home breadcrumb if requested
    if (includeHome) {
      const homeRoute = Routes.home
      items.push({
        label: customLabels.home || homeLabel,
        href: homeRoute.href[locale],
        isActive: pathname === homeRoute.href[locale]
      })
    }

    // Remove locale from pathname for processing
    const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/'
    
    // Skip if we're on home page
    if (pathWithoutLocale === '/' || pathname === Routes.home.href[locale]) {
      return items
    }

    // Find matching routes in the path hierarchy
    const pathSegments = pathWithoutLocale.split('/').filter(Boolean)
    let currentPath = `/${locale}`
    
    for (let i = 0; i < pathSegments.length; i++) {
      currentPath += `/${pathSegments[i]}`
      
      // Find the route that matches this path
      const routeEntry = Object.entries(Routes).find(([, route]) => {
        return route.href[locale] === currentPath
      })
      
      if (routeEntry) {
        const [routeKey, route] = routeEntry
        const isLast = i === pathSegments.length - 1
        
        items.push({
          label: customLabels[routeKey] || route.meta.title[locale] || pathSegments[i],
          href: isLast ? undefined : route.href[locale],
          isActive: isLast
        })
      } else {
        // If no route found, create a breadcrumb from the path segment
        const isLast = i === pathSegments.length - 1
        const segment = pathSegments[i]
        const segmentKey = segment.replace(/-/g, '_')
        
        items.push({
          label: customLabels[segmentKey] || formatSegmentLabel(segment),
          href: isLast ? undefined : currentPath,
          isActive: isLast
        })
      }
    }

    return items
  }

  return {
    breadcrumbs: generateBreadcrumbs(),
    pathname,
    locale
  }
}

// Helper function to format path segments into readable labels
function formatSegmentLabel(segment: string): string {
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Hook for generating breadcrumbs with route-specific logic
export function useRouteBreadcrumbs(customItems?: BreadcrumbItem[], options: UseBreadcrumbsOptions = {}) {
  const { breadcrumbs, pathname, locale } = useBreadcrumbs(options)
  
  // If custom items are provided, use them instead of auto-generated ones
  if (customItems) {
    const items: BreadcrumbItem[] = []
    
    if (options.includeHome !== false) {
      const homeRoute = Routes.home
      items.push({
        label: options.customLabels?.home || options.homeLabel || 'Home',
        href: homeRoute.href[locale],
        isActive: false
      })
    }
    
    items.push(...customItems)
    return { breadcrumbs: items, pathname, locale }
  }
  
  return { breadcrumbs, pathname, locale }
}
